import { useState } from 'react';
import { useTaskStore } from '@/lib/task-store';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { TASK_CATEGORIES, TASK_PRIORITIES, TASK_STATUSES } from '@/lib/constants/task';
import { useOwnerStore } from '@/lib/owner-store';
import { useEffect } from 'react';
import { DateInput } from '@/components/ui/date-input';

const inputStyles = "w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent";

export function TaskForm() {
  const { user } = useAuth();
  const { createTask } = useTaskStore();
  const { owners, fetchOwners } = useOwnerStore();
  
  const [formData, setFormData] = useState({
    title: '',
    category: 'Task',
    description: '',
    status: 'Open',
    priority: 'Medium',
    owner_id: '',
    date_assigned: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      await createTask({
        ...formData,
        created_by: user.id,
      });

      setFormData({
        title: '',
        category: 'Task',
        description: '',
        status: 'Open',
        priority: 'Medium',
        owner_id: '',
        date_assigned: new Date().toISOString().split('T')[0],
        due_date: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOwnerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'new') {
      window.dispatchEvent(new CustomEvent('showOwnersPage'));
      return;
    }
    setFormData(prev => ({ ...prev, owner_id: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-100">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={inputStyles}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-100">Category</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className={inputStyles}
            required
          >
            {TASK_CATEGORIES.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1 text-gray-100">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className={inputStyles}
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-100">Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className={inputStyles}
            required
          >
            {TASK_STATUSES.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-100">Priority</label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            className={inputStyles}
            required
          >
            {TASK_PRIORITIES.map(priority => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-100">Owner</label>
          <select
            name="owner_id"
            value={formData.owner_id}
            onChange={handleOwnerChange}
            className={inputStyles}
            required
          >
            <option value="">Select owner...</option>
            {owners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name} {owner.organization ? `(${owner.organization})` : ''}
              </option>
            ))}
            <option value="new" className="font-medium text-blue-600 dark:text-blue-400">
              ➕ Add New Owner
            </option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-100">Date Assigned</label>
          <DateInput
            name="date_assigned"
            value={formData.date_assigned}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-100">Due Date</label>
          <DateInput
            name="due_date"
            value={formData.due_date}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1 text-gray-100">Notes</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          className={inputStyles}
          rows={2}
        />
      </div>

      <Button type="submit" className="w-full">
        Create Task
      </Button>
    </form>
  );
}